import { arenaBoundsFor, bossBodyBoundsFor } from './balance'
import type { Boss, Player } from './types'

// 戴冠獣の「駆け抜け」(2026-10-10 比較版 → 2026-10-11 配布版の既定・DEV ?houndPassThrough=0 で旧＝出さない)。
//   10-11 答えるページ hound-pass-art「この動きで配布版へ入れる」。
//   ユーザー「全ボスに当てはまることだが、基本的にプレイヤーの方に距離を詰め続けるように動くため、移動が一方向になり続ける」
//   → 答え「推奨どおり」= 案1（主人公の脇を駆け抜けて、向こう側で振り返る技）を戴冠獣から。
//   出す条件は「同じ向きに一定時間流れ続けた時」と「主人公を壁へ押し込んだ時」の両方（どちらかで出やすくなる）。
//   必ず出すのではなく、くじで出やすくし、1幕に1〜2回まで。
//   調べ = docs/research/2026-10-10-boss-movement-one-direction.md・docs/research/2026-10-10-boss-spacing-ai-survey.md §6〜7。
//
// 当たりは無い（抜けるだけ）。理由：
//   - 戴冠獣は基本操作を学ばせる役（侍大将）。この技の仕事は「向きを変える」ことで、新しい防ぎ方の問題を足すことではない。
//   - 走りの絵は突進牙（弾く）と同じ絵を使っていた (10-11 に専用の低い全力疾走の絵へ替えたが、溜めの絵は今も突進牙の流用)。
//     当たりを付けると「突進牙と似た構えなのに弾けない／避ける」の見分けの問題が生まれる
//     (10-06 ユーザー「予備動作の見分けが付かない攻撃は対処のしようがない」)。当たりが無ければ見分けを外しても痛くない。
//   - 抜けた後の滑り止まりは背中を向けた隙 = 振り向いて殴れば報われる（「向き直る」を教える）。

let enabled = true
export function setHoundPassThrough(on: boolean): void { enabled = on }
export function houndPassThroughEnabled(): boolean { return enabled }

export const HOUND_PASS_ATTACK_ID = 'houndPassRun'

// 「同じ向きに流れ続けた」: 逆へ 20px 以上戻らずに 6 秒以上たち、その間に 120px 以上動いた（その場で殴り合っているだけは数えない）。
//   向きは問わない (主人公を追って流れても、押されて下がって流れても同じ「一方向」)。
//   区切り方は調べのシム (tests/combat_sim/boss_direction_probe.test.ts) と同じ。
export const HOUND_PASS_DRIFT_MS = 6000
export const HOUND_PASS_DRIFT_MIN_PX = 120
export const HOUND_PASS_DRIFT_BREAK_PX = 20
// どちらの条件でも、主人公と戴冠獣の左右がこの時間入れ替わっていない時だけ (回避でよく抜ける人には足さない＝位置が固まった時だけ)。
export const HOUND_PASS_SIDE_HELD_MS = 5000
// 「壁へ押し込んだ」: 二人が壁ぎわで詰まった時。どちらが壁を背負っていても数える:
//   - 主人公が壁を背負う: 主人公の背中側の壁まで 380px 以内。
//   - 戴冠獣が壁を背負う: 戴冠獣の背中側の体の壁まで 100px 以内 (HOUND_PASS_BOSS_WALL_PX)。
//     弾いて前へ出る遊び方だと押し込まれるのは戴冠獣の方 (シムの弾く bot: 主人公が前へ詰め、戴冠獣が右の壁に張り付いたまま
//     左右が一度も入れ替わらない)。ユーザーの言葉は「主人公を壁へ押し込んだ時」だが、流れが壁で止まって向きが変わらない点は同じなので
//     両方で出す (この読み替えは報告して確かめる)。
//   主人公側の 380px について:
//   調べの 200px ではない理由: 戴冠獣の体は壁の 120px 手前で止まる (balance.ts HOUND_WALL_INSET_DEFAULT_PX) ので、
//   壁から 270px より内側に追い込まれた主人公の向こうへは、振り返った頭が主人公に被らずには抜けられない (下の HOUND_PASS_MIN_PAST_PX)。
//   「押し込まれる手前」で抜けて流れを返す。壁に張り付かれてから後は今と同じ。
export const HOUND_PASS_WALL_PX = 380
export const HOUND_PASS_BOSS_WALL_PX = 100
// 抜けた先で、体の中心が主人公より最低でもこれだけ向こうに出られる時だけ出す = 振り返った時に頭 (中心から前へ 152px) が主人公 (半幅 15) にほぼ被らない (被り 17px まで)。
//   最初は 110 だったが、撮影で壁から 270px の主人公の向こうへ抜けると、振り返った頭が主人公に重なって窮屈だった (10-10)。170 (被りなし) と
//   比べ、出る場面を少し広げるため 150 にした (被り 17px は頭の先が触れる程度)。
export const HOUND_PASS_MIN_PAST_PX = 150
// 止まる所 = 主人公の向こう 190px（滑りを含む）。振り返ると好みの間合い (preferredRange 200) の少し手前。
export const HOUND_PASS_PAST_PX = 190
// くじ: 条件1つで 45%・両方で 75%。1幕に 2 回まで・前の駆け抜けから最低 3 手あける。
export const HOUND_PASS_ODDS_ONE = 0.45
export const HOUND_PASS_ODDS_BOTH = 0.75
export const HOUND_PASS_MAX_PER_ACT = 2
export const HOUND_PASS_MIN_ATTACKS_BETWEEN = 3

// 走り: 120ms で最高速 1000px/s まで上がり、そのまま主人公の脇を抜ける。止まりは 60px を 300ms で滑る (戻り recovery の頭)。
export const HOUND_PASS_SPEED = 1000
export const HOUND_PASS_ACCEL_MS = 120
export const HOUND_PASS_SKID_PX = 60
export const HOUND_PASS_SKID_MS = 300
// 振り返りの絵 (2026-10-11・boss_pose_timeline.ts houndPassRun の recovery)。recovery 750ms のうち:
//   0〜300ms 滑る (skid) → 420ms まで止まって背中を見せる → 首だけ振り返る (turnLook) → 590ms 体ごと向き直る途中 (turnFront)
//   → 750ms 待機へ入って向きが反転する。背中を見せている長さ (向きが変わるまで) は旧と同じ 750ms。
export const HOUND_PASS_TURN_LOOK_AT = 420 / 750
export const HOUND_PASS_TURN_FRONT_AT = 590 / 750

export function houndPassRunDistance(ms: number): number {
  const t = Math.max(0, ms)
  if (t < HOUND_PASS_ACCEL_MS) return HOUND_PASS_SPEED * t * t / (2 * HOUND_PASS_ACCEL_MS * 1000)
  return HOUND_PASS_SPEED * (t - HOUND_PASS_ACCEL_MS / 2) / 1000
}

export function houndPassRunMs(distance: number): number {
  const d = Math.max(0, distance)
  const ramp = houndPassRunDistance(HOUND_PASS_ACCEL_MS)
  return d <= ramp
    ? Math.sqrt(2 * d * HOUND_PASS_ACCEL_MS * 1000 / HOUND_PASS_SPEED)
    : HOUND_PASS_ACCEL_MS + (d - ramp) * 1000 / HOUND_PASS_SPEED
}

export function houndPassSkidDistance(ms: number): number {
  const u = Math.min(1, Math.max(0, ms / HOUND_PASS_SKID_MS))
  return HOUND_PASS_SKID_PX * (1 - (1 - u) ** 2)
}

// 滑り終わりの位置 (体の壁の内側)。
function finalX(playerX: number, dir: 1 | -1): number {
  const b = bossBodyBoundsFor('dream-hound')
  return Math.max(b.leftWall, Math.min(b.rightWall, playerX + dir * HOUND_PASS_PAST_PX))
}

// 主人公の向こうへどれだけ出られるか (体の中心・滑りを含めて止まる所)。
export function houndPassRoomPast(playerX: number, dir: 1 | -1): number {
  return (finalX(playerX, dir) - playerX) * dir
}

// 走りの計画 (active に入る時)。溜めの間に主人公が動いても、その時の主人公の向こうまで走る。
//   主人公が背中側へ抜けていたら、前へ 200px だけ走って止まる (追わない・振り返りは待機で)。
export function houndPassPlan(boss: Pick<Boss, 'x' | 'facing'>, playerX: number): { dir: 1 | -1; runMs: number; runEndX: number } {
  const dir: 1 | -1 = boss.facing === 'right' ? 1 : -1
  const b = bossBodyBoundsFor('dream-hound')
  const ahead = (playerX - boss.x) * dir > 0
  const end = ahead ? finalX(playerX, dir)
    : Math.max(b.leftWall, Math.min(b.rightWall, boss.x + dir * (200 + HOUND_PASS_SKID_PX)))
  const runEndX = end - dir * HOUND_PASS_SKID_PX
  const dist = Math.max(0, (runEndX - boss.x) * dir)
  return { dir, runMs: Math.max(1, houndPassRunMs(dist)), runEndX: boss.x + dir * dist }
}

export function isHoundPassRun(boss: Pick<Boss, 'id' | 'currentAttack' | 'state'>): boolean {
  return boss.id === 'dream-hound' && boss.currentAttack?.id === HOUND_PASS_ATTACK_ID
    && (boss.state === 'telegraph' || boss.state === 'active' || boss.state === 'recovery')
}

// ボスの流れの向き・時間と、左右が入れ替わらずにいる時間を毎 tick 数える (updateBoss の最後)。駆け抜けの間は流れを数え直す。
export function trackHoundDrift(prev: Boss, next0: Boss, dtMs: number, playerX: number): Boss {
  if (!enabled || next0.id !== 'dream-hound') return next0
  const side: 1 | -1 = playerX >= next0.x ? 1 : -1
  const held = (next0.houndSide === undefined || next0.houndSide === side) ? (next0.houndSideHeldMs ?? 0) + dtMs : 0
  const next = { ...next0, houndSide: side, houndSideHeldMs: held }
  if (isHoundPassRun(next) || next.state === 'staggered') {
    if (!next.houndDriftDir && !next.houndDriftMs) return next
    return { ...next, houndDriftDir: 0, houndDriftMs: 0, houndDriftBackPx: 0, houndDriftFromX: next.x }
  }
  let dir = next.houndDriftDir ?? 0
  let ms = next.houndDriftMs ?? 0
  let back = next.houndDriftBackPx ?? 0
  let from = next.houndDriftFromX ?? prev.x
  const dx = next.x - prev.x
  if (dir !== 0) ms += dtMs
  if (Math.abs(dx) >= 0.5) {
    const s: 1 | -1 = dx > 0 ? 1 : -1
    if (dir === 0) { dir = s; ms = 0; back = 0; from = prev.x }
    else if (s === dir) back = 0
    else {
      back += Math.abs(dx)
      if (back >= HOUND_PASS_DRIFT_BREAK_PX) { dir = s; ms = 0; back = 0; from = next.x }
    }
  }
  return { ...next, houndDriftDir: dir as 1 | -1 | 0, houndDriftMs: ms, houndDriftBackPx: back, houndDriftFromX: from }
}

export type HoundPassReason = { drift: boolean; wall: boolean }

// いま駆け抜けを出してよいか (くじの前)。出せない時は null。
export function houndPassReason(boss: Boss, player: Pick<Player, 'x'>): HoundPassReason | null {
  if (!enabled || boss.id !== 'dream-hound') return null
  if ((boss.houndSideHeldMs ?? 0) < HOUND_PASS_SIDE_HELD_MS) return null
  const count = boss.houndPassAct === boss.executesTaken ? boss.houndPassCount ?? 0 : 0
  if (count >= HOUND_PASS_MAX_PER_ACT) return null
  if (boss.houndPassAtAttack !== undefined && boss.attackCount - boss.houndPassAtAttack < HOUND_PASS_MIN_ATTACKS_BETWEEN) return null
  const dir: 1 | -1 = player.x >= boss.x ? 1 : -1
  if (houndPassRoomPast(player.x, dir) < HOUND_PASS_MIN_PAST_PX) return null
  const drift = !!boss.houndDriftDir && (boss.houndDriftMs ?? 0) >= HOUND_PASS_DRIFT_MS
    && Math.abs(boss.x - (boss.houndDriftFromX ?? boss.x)) >= HOUND_PASS_DRIFT_MIN_PX
  const arena = arenaBoundsFor('dream-hound')
  const playerWallRoom = dir > 0 ? arena.rightWall - player.x : player.x - arena.leftWall
  const body = bossBodyBoundsFor('dream-hound')
  const bossWallRoom = dir > 0 ? boss.x - body.leftWall : body.rightWall - boss.x
  const wall = playerWallRoom <= HOUND_PASS_WALL_PX || bossWallRoom <= HOUND_PASS_BOSS_WALL_PX
  return drift || wall ? { drift, wall } : null
}

export function houndPassOdds(reason: HoundPassReason): number {
  return reason.drift && reason.wall ? HOUND_PASS_ODDS_BOTH : HOUND_PASS_ODDS_ONE
}

// 駆け抜けに入る時の記録 (1幕の回数・最後に出した手番・流れの数え直し)。
export function houndPassEntryPatch(boss: Boss): Partial<Boss> {
  const count = boss.houndPassAct === boss.executesTaken ? boss.houndPassCount ?? 0 : 0
  return {
    houndPassAct: boss.executesTaken, houndPassCount: count + 1, houndPassAtAttack: boss.attackCount + 1,
    houndDriftDir: 0, houndDriftMs: 0, houndDriftBackPx: 0, houndDriftFromX: boss.x,
  }
}
